import React, { useState, useRef, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { getCountryCallingCode } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";
import flags from "react-phone-number-input/flags";
import ReactDOM from "react-dom";

const useStyles = makeStyles({
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#21212d",
    border: "1px solid #3a3a44",
    borderRadius: "5px",
    padding: "0 12px",
    minWidth: "100px",
    cursor: "pointer",
    transition: "border-color 0.15s",
    flexShrink: 0,
    "&:hover": {
      borderColor: "#69dfa5",
    },
  },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9998,
  },
  dropdown: {
    position: "fixed",
    backgroundColor: "#1a1a24",
    border: "1px solid #3a3a44",
    borderRadius: "8px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
    width: "290px",
    zIndex: 9999,
    overflow: "hidden",
  },
  searchWrap: {
    padding: "10px",
    borderBottom: "1px solid #2e2e3a",
  },
  search: {
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "#21212d",
    border: "1px solid #3a3a44",
    borderRadius: "5px",
    color: "#fff",
    fontSize: "13px",
    padding: "7px 10px",
    outline: "none",
    "&::placeholder": {
      color: "#5a5a6a",
    },
    "&:focus": {
      borderColor: "#69dfa5",
    },
  },
  list: {
    maxHeight: "230px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#1a1a24",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#3a3a44",
      borderRadius: "2px",
    },
  },
  empty: {
    color: "#5a5a6a",
    fontSize: "13px",
    padding: "16px",
    textAlign: "center",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    backgroundColor: "transparent",
    border: "none",
    color: "#e0e0e0",
    fontSize: "13px",
    padding: "8px 12px",
    cursor: "pointer",
    textAlign: "left",
    "&:hover": {
      backgroundColor: "#2a2a38",
    },
  },
  optionSelected: {
    backgroundColor: "#162a1f",
    color: "#69dfa5",
  },
  dialCode: {
    color: "#69dfa5",
    fontFamily: "monospace",
    fontSize: "12px",
    minWidth: "40px",
    flexShrink: 0,
  },
  countryName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

const CountrySelect = ({ value, onChange, countries }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const options = useMemo(
    () =>
      countries.map((iso) => ({
        value: iso,
        label: en[iso] || iso,
      })),
    [countries]
  );

  const currentOption = options.find((o) => o.value === value);

  let dialCode = "";
  if (value) {
    try {
      dialCode = `+${getCountryCallingCode(value)}`;
    } catch {}
  }

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((opt) => {
      if (opt.label.toLowerCase().includes(q)) return true;
      try {
        return getCountryCallingCode(opt.value).includes(q.replace(/^\+/, ""));
      } catch {
        return false;
      }
    });
  }, [options, search]);

  const openDropdown = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(true);
    setSearch("");
  }, []);

  const closeDropdown = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={classes.trigger}
        style={{
          backgroundColor: "#21212d",
          border: "1px solid #3a3a44",
          borderRadius: "5px",
          color: "#fff",
        }}
        onClick={open ? closeDropdown : openDropdown}
      >
        {value && flags[value] && (
          React.createElement(flags[value], {
            title: currentOption?.label,
            style: { width: 22, height: 16, display: "block", flexShrink: 0 },
          })
        )}
        <span
          style={{
            color: "#fff",
            fontFamily: "AlbertSansRegular, sans-serif",
            fontSize: "14px",
          }}
        >
          {dialCode}
        </span>
        <span
          style={{
            display: "inline-block",
            width: 0,
            height: 0,
            borderLeft: "4px solid transparent",
            borderRight: "4px solid transparent",
            borderTop: "5px solid #A6A8AA",
            flexShrink: 0,
          }}
        />
      </button>

      {open &&
        ReactDOM.createPortal(
          <>
            <div className={classes.overlay} onMouseDown={closeDropdown} />
            <div
              className={classes.dropdown}
              style={{ top: pos.top, left: pos.left }}
            >
              <div className={classes.searchWrap}>
                <input
                  autoFocus
                  className={classes.search}
                  placeholder="Buscar país o código..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className={classes.list}>
                {filtered.length === 0 ? (
                  <div className={classes.empty}>Sin resultados</div>
                ) : (
                  filtered.map((opt) => {
                    let code = "";
                    try {
                      code = `+${getCountryCallingCode(opt.value)}`;
                    } catch {}
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`${classes.option} ${
                          opt.value === value ? classes.optionSelected : ""
                        }`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          onChange(opt.value);
                          closeDropdown();
                        }}
                      >
                        {flags[opt.value] &&
                          React.createElement(flags[opt.value], {
                            title: opt.label,
                            style: {
                              width: 22,
                              height: 16,
                              display: "block",
                              flexShrink: 0,
                            },
                          })}
                        <span className={classes.dialCode}>{code}</span>
                        <span className={classes.countryName}>{opt.label}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

export default CountrySelect;
